// This is the Model Compliance drill-down page
// It shows when a user clicks a pie chart slice on the dashboard
// Devices are grouped by model and sorted by compliance severity

import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import config from "../config";
import Navbar from "../components/Navbar"
import ScheduleUpdateModal from "../components/ScheduleUpdateModal";
import useSuspended from "../hooks/useSuspended";

const API_URL = config.API_URL;

// Severity levels based on compliance percentage
function getSeverity(totalDrivers, outdatedDrivers, agingDrivers, stableDrivers, avgCompliance) {
  if (totalDrivers === 0) return { label: "No Data", color: "gray", order: 5 };
  // If we have breakdown data, use it
  if (outdatedDrivers > 0) return { label: "Critical", color: "red", order: 1 };
  if (agingDrivers > 0) return { label: "Aging", color: "yellow", order: 2 };
  if (stableDrivers > 0) return { label: "Stable", color: "blue", order: 3 };
  // Fall back to compliance percentage if no breakdown available
  if (avgCompliance < 85) return { label: "Critical", color: "red", order: 1 };
  return { label: "Good", color: "green", order: 4 };
}

function getSeverityStyles(color) {
  switch (color) {
    case "green": return { badge: "bg-green-900 text-green-300", border: "border-green-800", dot: "bg-green-500" };
    case "orange": return { badge: "bg-orange-900 text-orange-300", border: "border-orange-800", dot: "bg-orange-500" };
    case "yellow": return { badge: "bg-yellow-900 text-yellow-300", border: "border-yellow-800", dot: "bg-yellow-500" };
    case "blue": return { badge: "bg-blue-900 text-blue-300", border: "border-blue-800", dot: "bg-blue-400" };
    case "red": return { badge: "bg-red-900 text-red-300", border: "border-red-800", dot: "bg-red-500" };
    default: return { badge: "bg-gray-700 text-gray-400", border: "border-gray-700", dot: "bg-gray-500" };
  }
}

export default function ModelCompliance() {
  const { status } = useParams();  // "outdated", "up_to_date", or "unknown"
  const navigate = useNavigate();
  const isSuspended = useSuspended();
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [legacyModels, setLegacyModels] = useState({});
  const [referenceDevices, setReferenceDevices] = useState([]);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [selectedModel, setSelectedModel] = useState(null);
  const [selectedDevices, setSelectedDevices] = useState([]);
  const [diffData, setDiffData] = useState({}); // { [deviceId]: { loading, expanded, differences, referenceHostname } }

  // Get the org ID from logged in user
  const storedUser = JSON.parse(sessionStorage.getItem("user") || "{}");
  const ORG_ID = storedUser.organization_id;

  useEffect(() => {
    axios
      .get(`${API_URL}/organizations/${ORG_ID}/devices`)
      .then((res) => {
        setDevices(res.data);
        setLoading(false);
      })
      .catch(() => {
        setError("Failed to load device data");
        setLoading(false);
      });

    axios
      .get(`${API_URL}/organizations/${ORG_ID}/reference-devices`)
      .then((res) => setReferenceDevices(res.data))
      .catch(() => setReferenceDevices([]));
  }, [ORG_ID]);

  // Filter devices based on which card or pie slice was clicked
  const filteredDevices = devices.filter(device => {
    if (status === "up_to_date") return device.is_legacy !== "true" && device.compliance_pct >= 85;
    if (status === "outdated") return device.is_legacy !== "true" && device.total_drivers > 0 && device.compliance_pct < 85;
    if (status === "unknown") return device.is_legacy !== "true" && device.total_drivers === 0;
    if (status === "legacy") return device.is_legacy === "true";
    if (status === "all") return true;
    return false;
  });

  // Group devices by model
  const modelGroups = {};
  filteredDevices.forEach(device => {
    const modelKey = `${device.manufacturer || "Unknown"} ${device.model || "Unknown"}`;
    if (!modelGroups[modelKey]) {
      modelGroups[modelKey] = {
        manufacturer: device.manufacturer || "Unknown",
        model: device.model || "Unknown",
        devices: [],
        totalCompliance: 0,
        totalDrivers: 0
      };
    }
    modelGroups[modelKey].devices.push(device);
    modelGroups[modelKey].totalCompliance += device.compliance_pct;
    modelGroups[modelKey].totalDrivers += device.total_drivers;
  });

  // Calculate average compliance per model and sort by severity
  const sortedModels = Object.values(modelGroups)
    .map(group => ({
      ...group,
      avgCompliance: group.devices.length > 0
        ? Math.round(group.totalCompliance / group.devices.length)
        : 0,
      severity: getSeverity(
        group.totalDrivers,
        group.devices.reduce((sum, d) => sum + (d.outdated_drivers || 0), 0),
        group.devices.reduce((sum, d) => sum + (d.aging_drivers || 0), 0),
        group.devices.reduce((sum, d) => sum + (d.stable_drivers || 0), 0),
        Math.round(group.totalCompliance / group.devices.length)
      )
    }))
    .sort((a, b) => a.severity.order - b.severity.order);

  // Page title based on status
  const pageTitle = {
    outdated: "Devices with Outdated Drivers",
    up_to_date: "Compliant Devices",
    unknown: "Devices with Unknown Driver Status",
    legacy: "Legacy Devices",
    all: "All Device Models"
  }[status] || "Device Models";

  async function toggleDriverDiff(deviceId) {
    const existing = diffData[deviceId];

    // Already loaded — just toggle visibility, no need to re-fetch
    if (existing && existing.differences) {
      setDiffData(prev => ({ ...prev, [deviceId]: { ...existing, expanded: !existing.expanded } }));
      return;
    }

    setDiffData(prev => ({ ...prev, [deviceId]: { loading: true, expanded: true } }));
    try {
      const res = await axios.get(`${API_URL}/devices/${deviceId}/driver-diff`);
      setDiffData(prev => ({
        ...prev,
        [deviceId]: {
          loading: false,
          expanded: true,
          differences: res.data.differences,
          referenceHostname: res.data.reference_hostname
        }
      }));
    } catch (err) {
      setDiffData(prev => ({ ...prev, [deviceId]: { loading: false, expanded: true, differences: [], error: true } }));
    }
  }

  function toggleDevice(deviceId) {
    setSelectedDevices(prev =>
      prev.includes(deviceId) ? prev.filter(id => id !== deviceId) : [...prev, deviceId]
    );
  }

  function toggleGroupAll(groupDevices) {
    const ids = groupDevices.map(d => d.id);
    const allSelected = ids.every(id => selectedDevices.includes(id));
    if (allSelected) {
      setSelectedDevices(prev => prev.filter(id => !ids.includes(id)));
    } else {
      setSelectedDevices(prev => [...new Set([...prev, ...ids])]);
    }
  }

  async function deleteSelected() {
    if (selectedDevices.length === 0) return;
    if (!window.confirm(`Delete ${selectedDevices.length} device(s)? This will also delete all their driver records.`)) return;
    try {
      await Promise.all(selectedDevices.map(id => axios.delete(`${API_URL}/devices/${id}`, {
        headers: { Authorization: `Bearer ${sessionStorage.getItem("token")}` }
      })));
      setSelectedDevices([]);
      const res = await axios.get(`${API_URL}/organizations/${ORG_ID}/devices`);
      setDevices(res.data);
    } catch (err) {
      alert("Failed to delete one or more devices.");
    }
  }

  async function handleModelLegacy(manufacturer, model, currentlyLegacy) {
    const newValue = !currentlyLegacy;
    const modelKey = `${manufacturer} ${model}`;
    try {
      await axios.put(
        `${API_URL}/organizations/${ORG_ID}/models/legacy?model=${encodeURIComponent(model)}&manufacturer=${encodeURIComponent(manufacturer)}&is_legacy=${newValue}`,
        null,
        { headers: { Authorization: `Bearer ${sessionStorage.getItem("token")}` } }
      );
      setLegacyModels(prev => ({ ...prev, [modelKey]: newValue }));
    } catch (err) {
      alert("Failed to update legacy status");
    }
  }

  if (loading) return (
    <div className="flex items-center justify-center h-screen bg-gray-950">
      <p className="text-white text-xl">Loading...</p>
    </div>
  );

  if (error) return (
    <div className="flex items-center justify-center h-screen bg-gray-950">
      <p className="text-red-400 text-xl">{error}</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-950 text-white">

      <Navbar />

      {/* Main content */}
      <main className="px-8 py-8">

        {/* Page title */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-white">{pageTitle}</h1>
            <p className="text-gray-400 mt-1">
              {filteredDevices.length} device(s) · {sortedModels.length} model(s) · grouped by severity
            </p>
          </div>
          <button
            onClick={deleteSelected}
            disabled={selectedDevices.length === 0 || isSuspended}
            className={`text-sm px-4 py-2 rounded-lg transition-colors ${
              selectedDevices.length > 0 && !isSuspended
                ? "bg-red-700 hover:bg-red-600 text-white"
                : "bg-gray-700 text-gray-500 cursor-not-allowed"
            }`}
          >
            Delete Selected {selectedDevices.length > 0 && `(${selectedDevices.length})`}
          </button>
        </div>

        {/* Severity legend */}
        <div className="flex gap-6 mb-8">
          {[
            { label: "Critical", color: "red", range: "Has outdated drivers" },
            { label: "Aging", color: "yellow", range: "Has aging drivers" },
            { label: "Stable", color: "blue", range: "All drivers stable or current" },
            { label: "Good", color: "green", range: "Fully compliant" },
            { label: "No Data", color: "gray", range: "No drivers reported" },
          ].map(({ label, color, range }) => {
            const styles = getSeverityStyles(color);
            return (
              <div key={label} className="flex items-center gap-2">
                <div className={`w-3 h-3 rounded-full ${styles.dot}`}></div>
                <span className="text-gray-400 text-sm">{label} ({range})</span>
              </div>
            );
          })}
        </div>

        {/* No devices message */}
        {sortedModels.length === 0 && (
          <div className="bg-gray-900 rounded-xl p-12 border border-gray-800 text-center">
            <p className="text-gray-400 text-lg">No devices found in this category</p>
          </div>
        )}

        {/* Model groups */}
        <div className="space-y-4">
          {sortedModels.map((group) => {
            const styles = getSeverityStyles(group.severity.color);
            return (
              <div
                key={`${group.manufacturer}-${group.model}`}
                className={`bg-gray-900 rounded-xl p-6 border ${styles.border}`}
              >
                {/* Model header */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-3 h-3 rounded-full ${styles.dot}`}></div>
                    <div>
                      <p className="text-white font-semibold text-lg">{group.model}</p>
                      <p className="text-gray-400 text-sm">{group.manufacturer}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className={`text-sm px-3 py-1 rounded-full ${styles.badge}`}>
                      {group.severity.label}
                    </span>
                    <span className="text-gray-400 text-sm">
                      {group.devices.length} device(s) · avg {group.avgCompliance}% compliant
                    </span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        const modelKey = `${group.manufacturer} ${group.model}`;
                        const isCurrentlyLegacy = legacyModels[modelKey] ?? group.devices.every(d => d.is_legacy === "true");
                        handleModelLegacy(group.manufacturer, group.model, isCurrentlyLegacy);
                      }}
                      disabled={isSuspended}
                      className={`text-xs px-3 py-1 rounded-lg transition-colors border ${
                        isSuspended
                          ? "bg-gray-800 border-gray-700 text-gray-600 cursor-not-allowed"
                          : legacyModels[`${group.manufacturer} ${group.model}`] ?? group.devices.every(d => d.is_legacy === "true")
                          ? "bg-gray-700 border-gray-600 text-gray-300 hover:bg-gray-600"
                          : "bg-transparent border-gray-700 text-gray-400 hover:border-gray-500 hover:text-white"
                      }`}
                    >
                      {legacyModels[`${group.manufacturer} ${group.model}`] ?? group.devices.every(d => d.is_legacy === "true")
                        ? "✓ Legacy Model"
                        : "Mark as Legacy"}
                    </button>
                    {/* Update Agent button */}
                    <button
                      onClick={async (e) => {
                        e.stopPropagation();
                        if (!window.confirm(`Queue an agent update for all ${group.devices.length} ${group.manufacturer} ${group.model} device(s)?`)) return;
                        try {
                          const res = await axios.post(
                            `${API_URL}/organizations/${ORG_ID}/models/update-agent?manufacturer=${encodeURIComponent(group.manufacturer)}&model=${encodeURIComponent(group.model)}`,
                            null,
                            { headers: { Authorization: `Bearer ${sessionStorage.getItem("token")}` } }
                          );
                          alert(`Agent update queued for ${res.data.devices_queued} device(s). Agents will update on next check-in.`);
                        } catch (err) {
                          alert(err.response?.data?.detail || "Failed to queue agent update.");
                        }
                      }}
                      disabled={isSuspended}
                      className={`text-xs px-3 py-1 rounded-lg transition-colors border ${
                        isSuspended ? "bg-gray-800 border-gray-700 text-gray-600 cursor-not-allowed" : "bg-gray-700 border-gray-600 text-gray-300 hover:bg-gray-600"
                      }`}
                    >
                      Update Agent
                    </button>

                    {/* Update Drivers button — only for supported manufacturers */}
                    {["hp", "dell", "dell inc.", "lenovo"].includes(group.manufacturer?.toLowerCase()) && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedModel({ manufacturer: group.manufacturer, model: group.model, count: group.devices.length });
                          setShowUpdateModal(true);
                        }}
                        disabled={isSuspended}
                        className={`text-xs px-3 py-1 rounded-lg transition-colors border ${
                          isSuspended ? "bg-gray-800 border-gray-700 text-gray-600 cursor-not-allowed" : "bg-blue-900 border-blue-700 text-blue-300 hover:bg-blue-800"
                        }`}
                      >
                        Update Drivers
                      </button>
                    )}
                  </div>
                </div>

                {/* Device list for this model */}
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-gray-500 border-b border-gray-800">
                      <th className="py-2 w-8">
                        <input
                          type="checkbox"
                          checked={group.devices.every(d => selectedDevices.includes(d.id))}
                          onChange={() => toggleGroupAll(group.devices)}
                          className="cursor-pointer"
                          onClick={e => e.stopPropagation()}
                        />
                      </th>
                      <th className="text-left py-2">Hostname</th>
                      <th className="text-left py-2">OS Version</th>
                      <th className="text-left py-2">Compliance</th>
                      <th className="text-left py-2">Drivers</th>
                      <th className="text-left py-2">Last Seen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {group.devices.map(device => {
                      const isRef = referenceDevices.some(r => r.device_id === device.id);
                      const diff = diffData[device.id];
                      return (
                      <React.Fragment key={device.id}>
                      <tr
                        className="border-b border-gray-800 hover:bg-gray-800 transition-colors cursor-pointer"
                      >
                        <td className="py-2" onClick={e => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={selectedDevices.includes(device.id)}
                            onChange={() => toggleDevice(device.id)}
                            className="cursor-pointer"
                          />
                        </td>
                        <td className="py-2 text-white" onClick={() => navigate(`/devices/${device.id}`)}>
                          <div className="flex items-center gap-2">
                            {device.hostname}
                            {isRef && (
                              <span className="bg-blue-900 text-blue-300 text-xs px-2 py-0.5 rounded-full whitespace-nowrap">Reference</span>
                            )}
                          </div>
                        </td>
                        <td className="py-2 text-gray-400" onClick={() => navigate(`/devices/${device.id}`)}>{device.os_version}</td>
                        <td className="py-2" onClick={() => navigate(`/devices/${device.id}`)}>
                          <span className={`text-xs px-2 py-1 rounded-full ${styles.badge}`}>
                            {device.compliance_pct}%
                          </span>
                        </td>
                        <td className="py-2 text-gray-400" onClick={() => navigate(`/devices/${device.id}`)}>
                          {device.total_drivers} total · {device.up_to_date_drivers} up to date
                        </td>
                        <td className="py-2 text-gray-400">
                          <div className="flex items-center justify-between gap-2">
                            <span onClick={() => navigate(`/devices/${device.id}`)}>{new Date(device.last_seen).toLocaleDateString()}</span>
                            {!isRef && (
                              <button
                                onClick={(e) => { e.stopPropagation(); toggleDriverDiff(device.id); }}
                                className="text-blue-400 hover:text-blue-300 text-xs whitespace-nowrap"
                              >
                                {diff?.expanded ? "Hide diff" : "View differences"}
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                      {diff?.expanded && (
                        <tr className="border-b border-gray-800">
                          <td></td>
                          <td colSpan={5} className="py-3 pr-4">
                            {diff.loading ? (
                              <p className="text-gray-500 text-xs">Loading differences...</p>
                            ) : diff.error ? (
                              <p className="text-red-400 text-xs">Failed to load differences.</p>
                            ) : diff.differences.length === 0 ? (
                              <p className="text-green-400 text-xs">No differences from the reference device.</p>
                            ) : (
                              <div className="bg-gray-800 rounded-lg overflow-hidden">
                                <div className="px-3 py-2 text-xs text-gray-500 border-b border-gray-700">
                                  Compared to reference: <span className="text-gray-300">{diff.referenceHostname}</span> · {diff.differences.length} difference(s)
                                </div>
                                <table className="w-full text-xs">
                                  <thead>
                                    <tr className="text-gray-500 border-b border-gray-700">
                                      <th className="text-left py-1.5 px-3">Driver</th>
                                      <th className="text-left py-1.5 px-3">Reference</th>
                                      <th className="text-left py-1.5 px-3">This Device</th>
                                      <th className="text-left py-1.5 px-3">Issue</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {diff.differences.map((d, i) => (
                                      <tr key={i} className="border-b border-gray-700 last:border-0">
                                        <td className="py-1.5 px-3 text-gray-300">{d.driver_name}</td>
                                        <td className="py-1.5 px-3 text-gray-400">
                                          {d.reference_version ? `${d.reference_version} (${d.reference_date})` : "—"}
                                        </td>
                                        <td className="py-1.5 px-3 text-gray-400">
                                          {d.device_version ? `${d.device_version} (${d.device_date})` : "—"}
                                        </td>
                                        <td className="py-1.5 px-3">
                                          {d.diff_type === "missing_on_device" && <span className="text-red-400">Missing</span>}
                                          {d.diff_type === "version_mismatch" && <span className="text-yellow-400">Outdated</span>}
                                          {d.diff_type === "extra_on_device" && <span className="text-blue-400">Extra</span>}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                      </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            );
          })}
        </div>
        {/* Schedule Update Modal */}
        {showUpdateModal && selectedModel && (
          <ScheduleUpdateModal
            title={`Update Drivers — ${selectedModel.manufacturer} ${selectedModel.model}`}
            subtitle={`${selectedModel.count} device(s) will be queued for driver updates.`}
            onConfirm={async (scheduledAt) => {
              try {
                const url = scheduledAt
                  ? `${API_URL}/organizations/${ORG_ID}/models/update-drivers?manufacturer=${encodeURIComponent(selectedModel.manufacturer)}&model=${encodeURIComponent(selectedModel.model)}&scheduled_at=${encodeURIComponent(scheduledAt)}`
                  : `${API_URL}/organizations/${ORG_ID}/models/update-drivers?manufacturer=${encodeURIComponent(selectedModel.manufacturer)}&model=${encodeURIComponent(selectedModel.model)}`;
                const res = await axios.post(url, null, {
                  headers: { Authorization: `Bearer ${sessionStorage.getItem("token")}` }
                });
                setShowUpdateModal(false);
                setSelectedModel(null);
                alert(scheduledAt
                  ? `✅ Update scheduled for ${res.data.devices_queued} device(s) at ${new Date(scheduledAt).toLocaleString()}`
                  : `✅ Update queued for ${res.data.devices_queued} device(s). Agents will process on next check-in.`
                );
              } catch (err) {
                alert(err.response?.data?.detail || "❌ Failed to queue update. Please try again.");
              }
            }}
            onCancel={() => { setShowUpdateModal(false); setSelectedModel(null); }}
          />
        )}
      </main>
    </div>
  );
}